/**
 * Local-dev session helpers. This file is imported by production routes —
 * it must never contain default passwords or credential fallbacks.
 *
 * Test-login convenience (optional): copy
 *   src/config/devTestAccounts.local.example.js
 * to the gitignored
 *   src/config/devTestAccounts.local.js
 * Vite loads that module with import.meta.glob, which is an empty map
 * when the file is absent. Production builds never read it.
 */

/** True only while running `vite` / `npm run dev` — never in shipped bundles. */
export const IS_VITE_LOCAL_DEV =
  import.meta.env.PROD === false && import.meta.env.DEV === true

/**
 * Master switch for the test-account auth bypass.
 * VITE_ENABLE_TEST_ACCOUNTS is read only inside local dev.
 */
export const DEV_TEST_ACCOUNTS_ENABLED =
  IS_VITE_LOCAL_DEV && import.meta.env.VITE_ENABLE_TEST_ACCOUNTS !== 'false'

/**
 * True only for synthetic local bypass sessions (localStorage, no Firebase Auth).
 *
 * IMPORTANT: Do NOT use isAdmin / role === 'admin' / isTester here — those are
 * real Firestore profile fields and a Google-signed-in admin must still pass
 * manufacturing checkout.
 */
export function isLocalDevSessionUser(user) {
  if (!user) return false
  if (user.isLocalDevSession === true) return true
  // Legacy synthetic uids from buildDevSessionUser (admin-uid-… / test-uid-…)
  if (typeof user.uid === 'string' && /^(admin|test)-uid-/.test(user.uid)) return true
  return false
}

/** @deprecated Prefer isLocalDevSessionUser — kept for existing call sites. */
export function isDevTestSessionUser(user) {
  return isLocalDevSessionUser(user)
}

export function buildDevSessionUser(account) {
  const prefix = account.role === 'admin' ? 'admin' : 'test'
  return {
    uid: `${prefix}-uid-${Date.now()}`,
    email: account.email,
    name: account.label,
    subscription: account.subscription,
    role: account.role,
    isAdmin: account.role === 'admin',
    isTester: account.role === 'tester',
    isLocalDevSession: true,
    createdAt: new Date().toISOString(),
    designs: []
  }
}

/**
 * Synthetic localStorage session (email/password bypass) — not a Firebase Auth user.
 * Only relevant when the local-dev bypass is enabled for this build.
 */
export function isPersistedLocalSession(user) {
  if (!DEV_TEST_ACCOUNTS_ENABLED) return false
  return isLocalDevSessionUser(user)
}

function normalizeLocalAccounts(mod) {
  if (!mod) return []
  const raw = Array.isArray(mod.DEV_TEST_CREDENTIALS)
    ? mod.DEV_TEST_CREDENTIALS
    : []
  return raw.filter(
    (account) =>
      account &&
      typeof account.email === 'string' &&
      account.email.trim() &&
      typeof account.password === 'string' &&
      account.password
  )
}

/**
 * Optional local credential module. The glob is an empty map when the
 * gitignored file is absent. The DEV check keeps it out of production bundles.
 */
function readLocalAccounts() {
  if (!import.meta.env.DEV) return []
  const localAccountModules = import.meta.glob('./devTestAccounts.local.js', {
    eager: true
  })
  const mod = localAccountModules['./devTestAccounts.local.js']
  return normalizeLocalAccounts(mod)
}

/**
 * Match a local-dev test login. Production builds always return null and never
 * read credential files. Passwords live only in the gitignored local module.
 */
export async function matchDevLogin(email, password) {
  if (!import.meta.env.DEV || !DEV_TEST_ACCOUNTS_ENABLED) return null

  const accounts = readLocalAccounts()
  const normalized = (email || '').trim().toLowerCase()
  return (
    accounts.find(
      (account) =>
        normalized === String(account.email).trim().toLowerCase() &&
        password === account.password
    ) || null
  )
}
